import {
    MAIN_STYLE,
    MAIN_STYLE_GROUPS,
    BAR_IMAGE_SOURCE_STYLE,
    BAR_SLIDER_VALUE_PROPERTIES,
    SIGNED_PADDING_SIDES,
    LVGL_STYLE_INT_MIN,
    LVGL_STYLE_INT_MAX,
    VALUE_EVENTS,
} from '../shared.js'
import { OPENHASP_CONTROL_TYPE, OPENHASP_CONTROL_TYPE_VALUES } from '../../../controlTypes.js'
export const slider = {

    displayName: 'Slider',

    // LVGL-style parts — label/exportSuffix/order are the source of truth for
    // the Property Editor and for legacy JSONL wire keys (see v2/shared.js
    // part metadata doc and v2/wireFormat.js toWireKey).
    parts: {
        main: {
            label: 'Main',
            exportSuffix: '',
            order: 0,
            propertyGroups: [...MAIN_STYLE_GROUPS],
            properties: {
                ...MAIN_STYLE,
                ...BAR_IMAGE_SOURCE_STYLE,
                // BG padding is consumed by lv_bar.c/lv_slider.c for the
                // indicator track and slider value mapping.
                padding: { type: 'int', min: LVGL_STYLE_INT_MIN, max: LVGL_STYLE_INT_MAX, exportSpecial: true },
                ...SIGNED_PADDING_SIDES,
                keep_circle: { type: 'bool', internal: true },
            },
        },
        indicator: {
            label: 'Indicator',
            exportSuffix: '10',
            order: 1,
            properties: {
                bg_color:     { type: 'color' },
                ...BAR_IMAGE_SOURCE_STYLE,
                radius:       { type: 'int', min: 0, max: 999 },
                border_width: { type: 'int', min: 0 },
                // The indicator rect is drawn with a full rect descriptor including border
                // color (lv_bar.c:575-577, reused by slider's draw code); bar.js's own
                // indicator part already correctly exposes this — was missing here, an
                // inconsistency between two schemas sharing identical draw code.
                border_color: { type: 'color' },
                keep_circle:  { type: 'bool', internal: true },
            },
        },
        knob: {
            label: 'Knob',
            exportSuffix: '20',
            order: 2,
            properties: {
                bg_color:     { type: 'color' },
                img_src:      { type: 'resource', resourceType: 'image', label: 'Image source' },
                radius:       { type: 'int', min: 0, max: 999 },
                border_color: { type: 'color' },
                border_width: { type: 'int', min: 0 },
                // Legacy Designer shorthand: its value is the total extra
                // knob size and is exported as half on each LVGL side. New
                // edits use the four raw pad_* properties below.
                padding:      { type: 'int', min: LVGL_STYLE_INT_MIN, max: LVGL_STYLE_INT_MAX, exportSpecial: true },
                ...SIGNED_PADDING_SIDES,
                keep_circle:  { type: 'bool', internal: true },
            },
            // Do not fold uniform pad_*20 wire values back into legacy
            // `padding`; direct LVGL sides are authoritative for this part.
            paddingSidesAuthoritative: true,
        },
    },

    properties: {
        x: { type: 'int' }, y: { type: 'int' },
        width: { type: 'int', min: 0 }, height: { type: 'int', min: 0 },
        min_width: { type: 'int', min: 0 }, min_height: { type: 'int', min: 0 },
        haspid:   { type: 'int', min: 1, max: 254, internal: true },
        parentid: { type: 'int', min: 0, max: 254, internal: true },
        enabled: { type: 'bool' }, hidden: { type: 'bool' },
        val: { type: 'int' }, min: { type: 'int' }, max: { type: 'int' },
        ...BAR_SLIDER_VALUE_PROPERTIES,
        start_value: { type: 'int', visibleWhen: { property: 'type', equals: OPENHASP_CONTROL_TYPE.RANGE } },
        // Public openHASP enum: 0 Normal, 1 Reverse, 2 Symmetrical, 3 Range.
        // Firmware translates it explicitly to the LVGL 7 backend; these values
        // are deliberately not the native LVGL slider enum.
        type:     { type: 'enum', values: OPENHASP_CONTROL_TYPE_VALUES, label: 'Type' },
        groupid:  { type: 'int', min: 0, max: 15, exportSpecial: true },
        entityid: { type: 'string', internal: true },
    },

    defaults: {
        properties: {
            x: 0, y: 0, width: 100, height: 20, min_width: 20, min_height: 10,
            enabled: true, hidden: false,
            min: 0, max: 100, val: 0, step: 0, start_value: 0, type: 0,
        },
        styles: {
            // Real theme defaults (slider_init(), lv_theme_hasp.c:535-550, plus the generic
            // BORDER_WIDTH=HASP_DPX(2) constant applied via the shared "pretty"/"pretty_color"
            // styles, lv_theme_hasp.c:81/1214-1225): indicator.radius is a fixed 10px (not a
            // size-relative full pill — the previous 999 visibly diverges on a taller slider),
            // indicator.border_width is 2, knob.padding (slider_knob's pad_all) is
            // HASP_DPX(7) = 7, knob.border_width is 2.
            indicator: { radius: 10, border_width: 2 },
            knob:      { border_width: 2, padding: 7 },
        },
    },
    states: ['pressed', 'disabled'],
    editorCapabilities:  ['can_transform', 'can_copy', 'has_objectmenu'],
    runtimeCapabilities: ['has_action', 'has_data', 'has_group'],

    events: VALUE_EVENTS,

    convertTo: ['bar'],

    bindings: {
        properties: { val: {}, min: {}, max: {}, start_value: {}, indicator: { bg_color: {} } },
        actions: { hass_services: ['', 'homeassistant', 'openhasp', 'automation', 'climate', 'light', 'cover', 'fan', 'esphome'] },
    },

    automation: {
        triggerLabels: { up: 'Released' },
        preferredDataParameters: {
            'light.turn_on': ['brightness', 'brightness_pct', 'color_temp'],
        },
        runtimeVariables: [
            { name: 'val', type: 'int', label: 'Slider Value' },
            { name: 'start', type: 'int', label: 'Slider Range Start' },
        ],
        actionDomains: ['light', 'cover', 'fan', 'climate', 'homeassistant', 'openhasp', 'esphome'],
    },

    // Popup-template widgets bound to an entity (openhasp popup action —
    // see hassPopupUtils.js's generatePopupJsonl) read this HA attribute at
    // export time to reflect the entity's live value. Rendered unquoted
    // (numeric: true) and defaulted since a slider's val is a number, not
    // a string like cpicker's color.
    templateEntityBinding: { property: 'val', attribute: 'brightness', fallback: 128, numeric: true },
}
